import React, { useEffect, useCallback, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { navigate } from '@reach/router'
import { assign } from 'core-js/core/object'

import { getClassLight, bulkUpdateAnswers, getAssignment } from '_modules/class/actions'
import {
  getClassById,
  isBulkUpdateAnswerLoadingSelector,
  bulkUpdateAnswerErrorSelector,
  getAssignmentSelector,
  isGetAssignmentLoadingSelector,
} from '_modules/class/selectors'
import { usePrevious } from '_utils/hooks'
import Toastr, { ToastrTheme } from '_components/toastr'
import { ASSIGNMENT_TYPE } from '_constants/assignment'
import ASSIGNMENT_ACTIONS from '_modules/assignment/actions'
import { isCreatingAssignmentPostSelector } from '_modules/assignment/selectors'
import RocketLoader from '_components/rocket-loader'
import CallDetails from '_views/call-details'

import AssignmentInformation from './assignment-view'
import ViewAllResponses from './view-all-responses'
import styles from './styles.css'

const { createPost, editPost } = ASSIGNMENT_ACTIONS

const AssignmentDetail = ({ location, classId, assignmentId }) => {
  const courseClass = useSelector(state => getClassById(state, classId))
  const isUpdatingAnswers = useSelector(isBulkUpdateAnswerLoadingSelector)
  const wasUpdating = usePrevious(isUpdatingAnswers)
  const updateAnswerError = useSelector(bulkUpdateAnswerErrorSelector)
  const isPosting = useSelector(isCreatingAssignmentPostSelector)
  const assignment = useSelector(getAssignmentSelector)
  const isGetAssignmentLoading = useSelector(isGetAssignmentLoadingSelector)

  const [goNext, setNext] = useState(false)

  const dispatch = useDispatch()

  const isVideo = assignment?.type === ASSIGNMENT_TYPE.VIDEO
  const isNextAssignmentBlocked = !assignment?.nextAssignment?.id

  const isLastAssignment = !assignment?.nextAssignment?.id

  const nextAssignment = assignment?.nextAssignment

  const onCreatePost = useCallback(payload => dispatch(createPost(payload)), [dispatch])

  const onEditPost = useCallback(
    (payload, postId) =>
      dispatch(
        editPost({
          ...payload,
          postId,
          classId: Number(classId),
          assignmentId: Number(assignmentId),
        })
      ),
    [assignmentId, classId, dispatch]
  )

  useEffect(() => {
    dispatch(getClassLight(classId))
  }, [classId, dispatch])

  useEffect(() => {
    dispatch(getAssignment(classId, assignmentId))
  }, [classId, assignmentId, dispatch])

  useEffect(() => {
    if (wasUpdating && !isUpdatingAnswers) {
      if (updateAnswerError.size !== 0) {
        toast(
          <Toastr
            theme={ToastrTheme.ERROR}
            content="We couldn't update your answers, please, try again."
          />
        )
        return
      }

      if (goNext) {
        setNext(false)

        if (isNextAssignmentBlocked || isLastAssignment || !nextAssignment) {
          navigate([`/class/${classId}`])
          return
        }

        navigate([`/class/${classId}/assignment/${nextAssignment.id}`])
      }
    }
  }, [
    assignmentId,
    classId,
    goNext,
    isLastAssignment,
    isNextAssignmentBlocked,
    isUpdatingAnswers,
    nextAssignment,
    updateAnswerError.size,
    wasUpdating,
  ])

  const onSaveClick = useCallback(
    (answers, next = false) => {
      setNext(next)
      dispatch(bulkUpdateAnswers(Number(classId), Number(assignmentId), answers))
    },
    [assignmentId, classId, dispatch]
  )

  if (!courseClass.id || !assignment.id || isGetAssignmentLoading) {
    return (
      <div className={styles.loading}>
        <RocketLoader />
      </div>
    )
  }

  if (location.pathname.includes('responses')) {
    return <ViewAllResponses myClass={courseClass} assignment={assignment} />
  }

  return isVideo ? (
    <CallDetails assignment={assignment} nextAssignmentId={nextAssignment?.id} />
  ) : (
    <AssignmentInformation
      myClass={courseClass}
      assignment={assignment}
      onSaveAction={onSaveClick}
      isLoading={isUpdatingAnswers}
      hideNextButton={isLastAssignment}
      onCreatePost={onCreatePost}
      isPosting={isPosting}
      onEditPost={onEditPost}
      isLastAssignment={isLastAssignment}
    />
  )
}

AssignmentDetail.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  classId: PropTypes.string.isRequired,
  assignmentId: PropTypes.string.isRequired,
}

export default React.memo(AssignmentDetail)
